import React from "react";
import { useState, useEffect } from "react";
import * as G from "../../models/gear";
import { Box, Button, FormControl, InputLabel, MenuItem, Select } from "@mui/material";

interface GearProps {
    collection: string;
    onWait: (wait: boolean) => void;
}

interface Filters {
    type:string;
    year:string;
    make:string;
    model:string;
}

const Gear: React.FC<GearProps> = ({onWait, collection}) =>{
    const [filters, setFilters] = useState<Filters>({
        type: "",
        year: "",
        make: "",
        model: ""
    });
    const [sort, setSort] = useState<string>((collection === "inventory") ? "recent" : "yearasc");
    const [gear, setGear] = useState<G.default[]>([]);
    const [filterOptions, setFilterOptions] = useState<{ types: string[]; years: string[]; makes: string[]; models: string[] }>({ types: [], years: [], makes: [], models: [] });
    const [nextOffset, setNextOffset] = useState<number | null>(null);
    const [hasMoreResults, setHasMoreResults] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const literals = {
        inventory: { title: "GEAR FOR SALE", subtitle: "Amazing gear available for purchase.", title2: "AUTHENTICATED VINTAGE INSTRUMENTS", subtitle2: "This selection of guitars and related gear is presented here on consignment from private collectors</p><ul><li>Our Service: Each item has undergone a detailed, in-hand authentication and professional appraisal by CescosCorner, a letter of which will be provided to the buyer. We also facilitate high-quality photography and detailed descriptions for the seller.</li><li>Item Condition: Any setup, service, or maintenance was performed for the consigning private collector to ensure the instrument is presented optimally.</li><li>Next Steps: Contact us directly for any detailed information regarding the instrument's condition, sound, feel, shipping/delivery options, and to formally submit purchase proposals to the respective private owner.</li></ul><p>The inventory is updated daily.</p>"},
        portfolio: { title: "PORTFOLIO", subtitle: "Gallery of amazing past inventory.", title2: "ITEMS WE'VE SOLD", subtitle2: "Explore some of our former inventory that is now in the hands of happy players and collectors. Having a public portfolio is demonstrates our commitment to inventory accuracy and standing behind what we sell. It also serves as a great mechanism for sharing history."},
        personal: { title: "PERSONAL COLLECTION", subtitle: "Personal gear we're proud to share.", title2: "PERSONAL/NFS", subtitle2: "Here are some proud selections from our personal collection. Every guitar has a unique story and we are humbled to be the current caretakers of some incredible history."}
    };

    const fetchData = async (offset?: number, currentFilters = filters, currentSort = sort) => {
        setIsLoading(true);
        onWait(true);
        const params = new URLSearchParams({
            collection,
            limit: "40",
            sort: currentSort,
            ...(offset !== undefined && { offset: String(offset) }),
            ...(currentFilters.type && { type: currentFilters.type }),
            ...(currentFilters.year && { year: currentFilters.year }),
            ...(currentFilters.make && { make: currentFilters.make }),
            ...(currentFilters.model && { model: currentFilters.model }),
        });
        
        const response = await fetch(`/api/GetGear?${params.toString()}`);
        const json = await response.json();
        console.log("GetGear response:", json);

        if (offset !== undefined) {
            setGear(prevGear => [...prevGear, ...json.items]);
        } else {
            setGear(json.items);
        }
        setNextOffset(json.nextOffset ?? null);
        setHasMoreResults(json.hasMoreResults || false);
        setIsLoading(false);
        onWait(false);
    };

    // Reload when collection, filters, or sort changes
    useEffect(() => {
        setGear([]);
        setNextOffset(null);
        setHasMoreResults(false);
        fetchData(undefined, filters, sort);
    }, [collection, filters, sort]);

    // Load all available filter values (across every page) when the collection changes
    useEffect(() => {
        const fetchFilterOptions = async () => {
            const response = await fetch(`/api/GetGearFilters?collection=${collection}`);
            if (response.ok) {
                const json = await response.json();
                setFilterOptions({
                    types: json.types || [],
                    years: json.years || [],
                    makes: json.makes || [],
                    models: json.models || []
                });
            }
        };
        fetchFilterOptions();
    }, [collection]);

    const yearOptions = filterOptions.years.map((year:string, index: number) => (
        <MenuItem key={year} value={year}>{year}</MenuItem>
    ));
    const makeOptions = filterOptions.makes.map((make:string, index: number) => (
        <MenuItem key={make} value={make}>{make}</MenuItem>
    ));
    const modelOptions = filterOptions.models.map((model:string, index: number) => (
        <MenuItem key={model} value={model}>{model}</MenuItem>
    ));
    let sortItems = [
        <MenuItem value="yearasc">Year (old to new)</MenuItem>,
        <MenuItem value="yeardesc">Year (new to old)</MenuItem>
    ];
    if (collection === "inventory") {
        sortItems.unshift(<MenuItem value="recent">Recent</MenuItem>);
    }

    const items = gear.map((item:G.default, index:number) => (
        <figure className="port-item" key={item.path}>
            <a href={`/${collection}/${item.path}`}><img src={item.photos[0]} alt="portfolio item"/></a>
            <figcaption className="port-desc">
                <p>{(collection === "inventory") ? item.price : item.year}</p>
                <a href={`/${collection}/${item.path}`} className="port-button">{item.title}</a>
            </figcaption>
        </figure>
    ));
    // return the JSX
    return (
        <div>
            <section className={`section-hero parallax--bg ${collection}`}>
                <div className="container">
                    <h1 className="parallax--box">{(literals as any)[collection].title}</h1>
                    <p>{(literals as any)[collection].subtitle}</p>
                </div>
            </section>
            <div className="container2">
	            <h2>{(literals as any)[collection].title2}</h2>
	            <div dangerouslySetInnerHTML={{
                        __html: (literals as any)[collection].subtitle2,
                    }} 
                />
            </div>
            <div className="filter-bar">
                <div className="filters-inline">
                    <Box sx={{ minWidth: 160 }}>
                        <FormControl fullWidth>
                            <InputLabel id="typeLabel">Type</InputLabel>
                            <Select labelId="typeLabel" id="typeSelect1" name="type" label="Type" value={filters.type} onChange={(e) => {setFilters(f => ({...f, type: e.target.value as string}))}}>
                                <MenuItem value=""></MenuItem>
                                <MenuItem value="Electric Guitar">Electric Guitar</MenuItem>
                                <MenuItem value="Acoustic Guitar">Acoustic Guitar</MenuItem>
                                <MenuItem value="Bass Guitar">Bass Guitar</MenuItem>
                                <MenuItem value="Amplifier">Amplifier</MenuItem>
                                <MenuItem value="Pedal/Effect">Pedal/Effect</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ minWidth: 160 }}>
                        <FormControl fullWidth>
                            <InputLabel id="yearLabel">Year</InputLabel>
                            <Select labelId="yearLabel" id="yearSelect" name="year" label="Year" value={filters.year} onChange={(e) => {setFilters(f => ({...f, year: e.target.value as string}))}}>
                                <MenuItem value=""></MenuItem>
                                {yearOptions}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ minWidth: 160 }}>
                        <FormControl fullWidth>
                            <InputLabel id="makeLabel">Make</InputLabel>
                            <Select labelId="makeLabel" id="makeSelect" name="make" label="Make" value={filters.make} onChange={(e) => {setFilters(f => ({...f, make: e.target.value as string}))}}>
                                <MenuItem value=""></MenuItem>
                                {makeOptions}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ minWidth: 160 }}>
                        <FormControl fullWidth>
                            <InputLabel id="modelLabel">Model</InputLabel>
                            <Select labelId="modelLabel" id="modelSelect" name="model" label="Model" value={filters.model} onChange={(e) => {setFilters(f => ({...f, model: e.target.value as string}))}}>
                                <MenuItem value=""></MenuItem>
                                {modelOptions}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ minWidth: 160 }}>
                        <FormControl fullWidth>
                            <InputLabel id="sortLabel">Sort</InputLabel>
                            <Select labelId="sortLabel" id="sortSelect" name="sort" label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>
                                {sortItems}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ minWidth: 160 }} style={(filters.type === "" && filters.year === "" && filters.make === "" && filters.model === "")? {display: "none"} : {display: "flex", paddingTop: "10px"}}>
                        <FormControl fullWidth>
                            <Button onClick={() => setFilters({type: "",year: "",make: "",model: ""})}>Clear Filters</Button>
                        </FormControl>
                    </Box>
                </div>
                <div className="filters-collapsed">
                    
                </div>
            </div>
            <section className="portfolio">
                {items}
            </section>
            {isLoading && <p>Loading...</p>}
            {!isLoading && hasMoreResults && nextOffset !== null && (
                <Box sx={{ display: "flex", justifyContent: "center", margin: 3 }}>
                    <Button variant="outlined" onClick={() => fetchData(nextOffset)}>Load More</Button>
                </Box>
            )}
        </div>
    );
}

export default Gear;
